import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text, TouchableOpacity, ScrollView, ActivityIndicator, TextInput, Alert } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { Ionicons } from '@expo/vector-icons';
import { logoutUser } from '../store/slices/authSlice';
import pilotReviewService from '../services/pilotReviewService';
import userService from '../services/userService';
import apiClient from '../utils/axiosConfig';

const ProfileScreen = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [pilotStats, setPilotStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingUserStats, setLoadingUserStats] = useState(false);

  // Estado cambio de contraseña
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  
  // Cargar estadísticas del usuario
  useEffect(() => {
    if (user) {
      loadUserStats();
      if (user.is_pilot) {
        const pilotId = user.pilot_id || user.pilotuser_id;
        if (pilotId) {
          loadPilotStats(pilotId);
        } else {
          // pilot_id no en Redux — cargar desde API
          loadPilotIdAndStats();
        }
      }
    }
  }, [user]);

  const loadPilotIdAndStats = async () => {
    try {
      setLoadingStats(true);
      const response = await apiClient.get('/me/');
      const pid = response.data?.pilot_id;
      if (pid) loadPilotStats(pid);
    } catch (_) {
      setLoadingStats(false);
    }
  };
  
  const loadUserStats = async () => {
    try {
      setLoadingUserStats(true);
      const stats = await userService.getUserStats();
      setUserStats(stats);
    } catch (error) {
      console.error('Error al cargar estadísticas del usuario:', error);
    } finally {
      setLoadingUserStats(false);
    }
  };
  
  const loadPilotStats = async (pilotId) => {
    try {
      setLoadingStats(true);
      const stats = await pilotReviewService.getPilotStats(pilotId);
      setPilotStats(stats);
    } catch (error) {
      console.error('Error al cargar estadísticas del piloto:', error);
    } finally {
      setLoadingStats(false);
    }
  };
  
  // Renderizar estrellas
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Ionicons key={i} name="star" size={16} color="#f39c12" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Ionicons key="half" name="star-half" size={16} color="#f39c12" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Ionicons key={`empty-${i}`} name="star-outline" size={16} color="#f39c12" />);
    }
    
    return stars;
  };
  
  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Determinar el rol del usuario
  const getUserRole = () => {
    if (user?.is_staff) return 'Administrador';
    if (user?.is_pilot) return 'Piloto';
    if (user?.is_enterprise_user) return 'Usuario de Empresa';
    return 'Usuario';
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      Alert.alert('Error', 'Todos los campos son requeridos.');
      return;
    }
    if (newPassword.length < 8) {
      Alert.alert('Error', 'La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      Alert.alert('Error', 'Las contraseñas nuevas no coinciden.');
      return;
    }
    try {
      setChangingPassword(true);
      await userService.changePassword(currentPassword, newPassword);
      Alert.alert('Listo', 'Contraseña actualizada correctamente.');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const msg = error.response?.data?.error || 'No se pudo cambiar la contraseña.';
      Alert.alert('Error', msg);
    } finally {
      setChangingPassword(false);
    }
  };



  return (
    <ScrollView style={styles.container}>
      <View style={styles.profileHeader}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}</Text>
        </View>
        <Text style={styles.name}>{user?.name || user?.email || 'Usuario'}</Text>
        <Text style={styles.role}>{getUserRole()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Información Personal</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Email:</Text>
          <Text style={styles.infoValue}>{user?.email || 'No disponible'}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>ID:</Text>
          <Text style={styles.infoValue}>{user?.id || 'No disponible'}</Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Estadísticas</Text>
        {user?.is_pilot && userStats?.missions && (
          <>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Misiones:</Text>
              <Text style={styles.infoValue}>{userStats.missions.total} asignadas</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Completadas:</Text>
              <Text style={styles.infoValue}>{userStats.missions.completed} misiones</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Pendientes:</Text>
              <Text style={styles.infoValue}>{userStats.missions.pending} misiones</Text>
            </View>
          </>
        )}
        
        {user?.is_enterprise_user && userStats?.reports && (
          <>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Reportes:</Text>
              <Text style={styles.infoValue}>{userStats.reports.total} totales</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Completados:</Text>
              <Text style={styles.infoValue}>{userStats.reports.completed} reportes</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Pendientes:</Text>
              <Text style={styles.infoValue}>{userStats.reports.pending} reportes</Text>
            </View>
          </>
        )}
        
        {user?.is_staff && userStats && (
          <>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Misiones:</Text>
              <Text style={styles.infoValue}>{userStats.missions?.total || 0} totales</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Completadas:</Text>
              <Text style={styles.infoValue}>{userStats.missions?.completed || 0} misiones</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Pendientes:</Text>
              <Text style={styles.infoValue}>{userStats.missions?.pending || 0} misiones</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Reportes:</Text>
              <Text style={styles.infoValue}>{userStats.reports?.total || 0} totales</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>R. Completados:</Text>
              <Text style={styles.infoValue}>{userStats.reports?.completed || 0} reportes</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>R. Pendientes:</Text>
              <Text style={styles.infoValue}>{userStats.reports?.pending || 0} reportes</Text>
            </View>
          </>
        )}
        
        {!user?.is_pilot && !user?.is_enterprise_user && !user?.is_staff && (
          <View style={styles.infoRow}>
            <Text style={styles.infoValue}>No hay estadísticas disponibles</Text>
          </View>
        )}
      </View>
      
      {/* Sección de reseñas para pilotos */}
      {user?.is_pilot && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Reseñas</Text>
          {loadingStats ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color="#3498db" />
              <Text style={styles.loadingText}>Cargando reseñas...</Text>
            </View>
          ) : pilotStats ? (
            <>
              {/* Promedio de estrellas */}
              <View style={styles.ratingContainer}>
                <View style={styles.starsContainer}>
                  {renderStars(pilotStats.average_rating)}
                </View>
                <Text style={styles.ratingText}>
                  {pilotStats.average_rating.toFixed(1)} ({pilotStats.total_reviews} reseñas)
                </Text>
              </View>
              
              {/* Últimas 3 reseñas */}
              {pilotStats.recent_reviews && pilotStats.recent_reviews.length > 0 ? (
                <>
                  <Text style={styles.recentReviewsTitle}>Últimas reseñas:</Text>
                  {pilotStats.recent_reviews.map((review, index) => (
                    <View key={review.id || index} style={styles.reviewItem}>
                      <View style={styles.reviewHeader}>
                        <View style={styles.reviewStars}>
                          {renderStars(review.rating)}
                        </View>
                        <Text style={styles.reviewDate}>{formatDate(review.created_at)}</Text>
                      </View>
                      {review.comment && (
                        <Text style={styles.reviewComment}>{review.comment}</Text>
                      )}
                      <Text style={styles.reviewMission}>
                        Misión #{review.mission_sid || 'N/A'} - {review.enterprise_name || 'N/A'}
                      </Text>
                    </View>
                  ))}
                </>
              ) : (
                <Text style={styles.noReviewsText}>Aún no tienes reseñas</Text>
              )}
            </>
          ) : (
            <Text style={styles.noReviewsText}>No se pudieron cargar las reseñas</Text>
          )}
        </View>
      )}
      
      <TouchableOpacity
        style={styles.changePasswordButton}
        onPress={() => setShowPasswordForm(!showPasswordForm)}
      >
        <View style={styles.changePasswordIcon}>
          <Ionicons name="lock-closed" size={18} color="white" />
        </View>
        <Text style={styles.changePasswordButtonText}>Cambiar contraseña</Text>
        <Ionicons name={showPasswordForm ? 'chevron-up' : 'chevron-forward'} size={18} color="#bdc3c7" />
      </TouchableOpacity>

      {showPasswordForm && (
        <View style={[styles.section, { marginHorizontal: 15, borderRadius: 12 }]}>
          <Text style={styles.sectionTitle}>Nueva contraseña</Text>

          <View style={styles.passwordRow}>
            <TextInput
              style={styles.passwordInput}
              value={currentPassword}
              onChangeText={setCurrentPassword}
              placeholder="Contraseña actual"
              secureTextEntry={!showCurrent}
              autoCapitalize="none"
            />
            <TouchableOpacity onPress={() => setShowCurrent(v => !v)} style={styles.eyeButton}>
              <Ionicons name={showCurrent ? 'eye-off-outline' : 'eye-outline'} size={20} color="#7f8c8d" />
            </TouchableOpacity>
          </View>

          <View style={styles.passwordRow}>
            <TextInput
              style={styles.passwordInput}
              value={newPassword}
              onChangeText={setNewPassword}
              placeholder="Nueva contraseña (mín. 8 caracteres)"
              secureTextEntry={!showNew}
              autoCapitalize="none"
            />
            <TouchableOpacity onPress={() => setShowNew(v => !v)} style={styles.eyeButton}>
              <Ionicons name={showNew ? 'eye-off-outline' : 'eye-outline'} size={20} color="#7f8c8d" />
            </TouchableOpacity>
          </View>

          <View style={styles.passwordRow}>
            <TextInput
              style={styles.passwordInput}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              placeholder="Confirmar nueva contraseña"
              secureTextEntry={!showConfirm}
              autoCapitalize="none"
            />
            <TouchableOpacity onPress={() => setShowConfirm(v => !v)} style={styles.eyeButton}>
              <Ionicons name={showConfirm ? 'eye-off-outline' : 'eye-outline'} size={20} color="#7f8c8d" />
            </TouchableOpacity>
          </View>

          <View style={styles.formButtons}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => { setShowPasswordForm(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}
            >
              <Text style={styles.cancelButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.submitButton}
              onPress={handleChangePassword}
              disabled={changingPassword}
            >
              {changingPassword
                ? <ActivityIndicator size="small" color="white" />
                : <Text style={styles.submitButtonText}>Guardar</Text>
              }
            </TouchableOpacity>
          </View>
        </View>
      )}

      <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
        <Ionicons name="log-out-outline" size={20} color="white" style={styles.logoutIcon} />
        <Text style={styles.logoutButtonText}>Cerrar sesión</Text>
      </TouchableOpacity>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  profileHeader: {
    backgroundColor: 'white',
    alignItems: 'center',
    padding: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#2c3e50',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  avatarText: {
    color: 'white',
    fontSize: 36,
    fontWeight: 'bold',
  },
  name: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  role: {
    fontSize: 16,
    color: '#7f8c8d',
    marginTop: 5,
  },
  section: {
    backgroundColor: 'white',
    padding: 20,
    marginVertical: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 15,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  infoLabel: {
    fontWeight: 'bold',
    width: 100,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    color: '#2c3e50',
  },
  logoutButton: {
    backgroundColor: '#e74c3c',
    borderRadius: 12,
    padding: 15,
    margin: 15,
    marginTop: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  logoutButtonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  logoutIcon: {
    marginRight: 10,
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: '#7f8c8d',
    fontSize: 14,
  },
  ratingContainer: {
    alignItems: 'center',
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  starsContainer: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  ratingText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  recentReviewsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  reviewItem: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#3498db',
  },
  reviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  reviewStars: {
    flexDirection: 'row',
  },
  reviewDate: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  reviewComment: {
    fontSize: 14,
    color: '#2c3e50',
    marginBottom: 8,
    fontStyle: 'italic',
  },
  reviewMission: {
    fontSize: 12,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  noReviewsText: {
    textAlign: 'center',
    color: '#7f8c8d',
    fontStyle: 'italic',
    padding: 20,
  },
  changePasswordButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 16,
    marginTop: 10,
    marginHorizontal: 15,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  changePasswordIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  changePasswordButtonText: {
    flex: 1,
    fontSize: 16,
    color: '#2c3e50',
    fontWeight: '500',
  },
  passwordRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginBottom: 12,
    backgroundColor: '#fafafa',
  },
  passwordInput: {
    flex: 1,
    padding: 14,
    fontSize: 15,
    color: '#2c3e50',
  },
  eyeButton: {
    padding: 12,
  },
  formButtons: {
    flexDirection: 'row',
    marginTop: 4,
    gap: 10,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#ecf0f1',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#2c3e50',
    fontWeight: '600',
    fontSize: 15,
  },
  submitButton: {
    flex: 1,
    backgroundColor: '#2c3e50',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonText: {
    color: 'white',
    fontWeight: '600',
    fontSize: 15,
  },
});

export default ProfileScreen;